import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { AccessStore } from '../stores/access.store';
import {
  holdsRequirement,
  type RequiredPermission,
} from '../shared/access/permission-requirement';

/**
 * Route-guard factory for the permission model (roles overhaul §11.1):
 *
 *   canActivate: [AuthGuard, permissionGuard('members.view')]
 *   canActivate: [AuthGuard, permissionGuard([a, b], { orgWide: true })]  // all of
 *   canActivate: [AuthGuard, permissionGuard([{ key: a, orgWide: true }, b])] // mixed
 *
 * Awaits the access profile (so a cold deep-link works), then requires the
 * permission in the active org. Fails CLOSED — a failed profile load or a
 * missing permission routes to /access-denied instead of silently bouncing
 * home, so the user learns why the page refused them.
 */
export interface PermissionGuardOptions {
  /**
   * Default scope for bare keys: true requires them ORG-WIDE (base role ∪
   * grants), ignoring module bindings — for pages that span the org rather
   * than sit inside a module. Mirrors `NavigationItem.orgWide` so the nav and
   * the route agree. A `{ key, orgWide }` entry overrides it for that key.
   */
  readonly orgWide?: boolean;
}

export const permissionGuard = (
  permission: RequiredPermission | readonly RequiredPermission[],
  options: PermissionGuardOptions = {}
): CanActivateFn => {
  return () => {
    const accessStore = inject(AccessStore);
    const router = inject(Router);
    return accessStore.ensureLoaded().pipe(
      map((profile) => {
        // ALL of them, each at its own scope — mirrors
        // `NavigationItem.requiredPermission` through the shared rule.
        const allowed = holdsRequirement(
          accessStore,
          permission,
          options.orgWide === true
        );
        return profile !== null && allowed
          ? true
          : router.createUrlTree(['/access-denied']);
      })
    );
  };
};
